import React from "react";

import { productAnalytics, useTranslation } from "utils";

import {
  FORECAST_PRESET_CUSTOM,
  FORECAST_PRESET_ORDER,
  ForecastPreset,
  INPUT_COST_PRESET_PCT,
  OUTPUT_COST_PRESET_PCT,
  TIMELINE_VALUES,
  TimelineOption,
} from "consts";

import { Button, Form, Icon, Option, Select, Tooltip } from "components";

import { ScenarioSettings } from "pages/app/scenario-analysis/types/scenario";
import { PresetPctTable, customPresetLabel, pctFromPreset, presetFromPct, presetLabel } from "pages/app/scenario-analysis/utils/forecastPresets";
import { tourTargetProps } from "pages/app/scenario-analysis/utils/outcomesTourTarget";
import { getSettingsBarChange } from "pages/app/scenario-analysis/utils/soilSightTracking";

/** Icon leading each control's label, per the prototype. */
const CONTROL_ICONS = {
  inputCosts: "tabler/coin",
  outputPrices: "tabler/trending-up",
  timeline: "tabler/clock",
} as const;

/** Which CTA the bar shows on the right. The container derives this; the bar only renders it. */
export type ForecastPrimaryAction = "recalculate" | "calculating";

export interface ForecastSettingsBarProps {
  settings: ScenarioSettings;
  /** The container runs the pick now, or leaves it for the tab dots. */
  onSettingsChange: (settings: ScenarioSettings) => void;
  /** Null while nothing is pending. The slot then stays empty and shows no stand-in button. */
  primaryAction: ForecastPrimaryAction | null;
  onPrimaryAction: () => void;
  primaryActionBlockedReason?: string | null;
  onAddScenario: () => void;
  onEditScenarios: () => void;
  /** Opens the project settings drawer, unchanged. */
  onMoreFilters: () => void;
  /** False on the Scenarios tab, where the scenario card supplies Add and Edit itself. */
  showScenarioActions?: boolean;
  disableAddScenario?: boolean;
  dataTestId?: string;
}

type BarSelectOption<T extends string | number> = { value: T; label: string; disabled?: boolean };

type BarSelectProps<T extends string | number> = {
  iconName: string;
  label: string;
  value: T;
  options: BarSelectOption<T>[];
  onChange: (value: T) => void;
  dataTestId: string;
};

/** The bar's one control shape: icon and grey label outside, the value in its own bordered dropdown. */
const BarSelect = <T extends string | number>({ iconName, label, value, options, onChange, dataTestId }: BarSelectProps<T>) => (
  <div className="forecast-settings-bar-control" data-testid={dataTestId}>
    <span className="forecast-settings-bar-control-label">
      <Icon iconName={iconName} size="s" className="forecast-settings-bar-control-icon" aria-hidden />
      <span className="forecast-settings-bar-control-label-text">{label}</span>
    </span>
    {/* The wrapper owns the outline: `floating-select-reset` strips border and background off the Select itself. */}
    <span className="forecast-settings-bar-value">
      <Select
        value={value}
        onChange={onChange}
        popupMatchSelectWidth={false}
        popupClassName="forecast-settings-bar-dropdown"
        className="forecast-settings-bar-value-select"
        dataTestId={`${dataTestId}-select`}
        virtual={false}
      >
        {options.map((option) => (
          <Option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </Option>
        ))}
      </Select>
    </span>
  </div>
);

/** Preset options for a trend control, with the drawer-set percentage kept visible but unselectable. */
const presetOptions = (pct: number, table: PresetPctTable, t: (key: string, options?: Record<string, unknown>) => string): BarSelectOption<string>[] => {
  const matched = presetFromPct(pct, table);
  const options: BarSelectOption<string>[] = FORECAST_PRESET_ORDER.map((preset) => ({ value: preset, label: presetLabel(preset, t) }));
  if (matched === null) {
    options.push({ value: FORECAST_PRESET_CUSTOM, label: customPresetLabel(pct, t), disabled: true });
  }
  return options;
};

export const ForecastSettingsBar = ({
  settings,
  onSettingsChange,
  primaryAction,
  onPrimaryAction,
  primaryActionBlockedReason = null,
  onAddScenario,
  onEditScenarios,
  onMoreFilters,
  showScenarioActions = true,
  disableAddScenario = false,
  dataTestId = "forecast-settings-bar",
}: ForecastSettingsBarProps) => {
  const { t } = useTranslation("scenarioAnalysis");

  const handleChange = <K extends keyof ScenarioSettings>(key: K, value: ScenarioSettings[K]) => {
    const next = { ...settings, [key]: value };
    const change = getSettingsBarChange(settings, next);
    if (change) productAnalytics.track("soilsights:settings_update", change);
    onSettingsChange(next);
  };

  const renderPrimaryAction = () => {
    if (primaryAction === null) return null;

    if (primaryAction === "calculating") {
      return (
        <Tooltip title={t("forecastSettingsBar.calculating")} placement="top">
          <Button type="primary" size="middle" loading disabled className="forecast-settings-bar-primary" dataTestId={`${dataTestId}-calculating`}>
            <span className="forecast-settings-bar-action-label">{t("forecastSettingsBar.calculating")}</span>
          </Button>
        </Tooltip>
      );
    }

    const label = t("forecastSettingsBar.recalculate");
    const isBlocked = Boolean(primaryActionBlockedReason);

    return (
      <Tooltip title={primaryActionBlockedReason ?? label} placement="top">
        <Button
          type="primary"
          size="middle"
          onClick={onPrimaryAction}
          disabled={isBlocked}
          icon={<Icon iconName="tabler/refresh" size={20} color="white" />}
          iconLeft
          className="forecast-settings-bar-primary"
          dataTestId={`${dataTestId}-primary`}
        >
          <span className="forecast-settings-bar-action-label">{label}</span>
        </Button>
      </Tooltip>
    );
  };

  return (
    // `component={false}` keeps Form context for the Form.Items that `Select` renders, without a nested <form>.
    <Form component={false}>
      <div className="forecast-settings-bar" data-testid={dataTestId} {...tourTargetProps("settings-bar")}>
        <div className="forecast-settings-bar-controls">
          <BarSelect
            iconName={CONTROL_ICONS.inputCosts}
            label={t("forecastSettingsBar.inputCosts")}
            value={presetFromPct(settings.costs, INPUT_COST_PRESET_PCT) ?? FORECAST_PRESET_CUSTOM}
            options={presetOptions(settings.costs, INPUT_COST_PRESET_PCT, t)}
            onChange={(preset) => handleChange("costs", pctFromPreset(preset as ForecastPreset, INPUT_COST_PRESET_PCT))}
            dataTestId={`${dataTestId}-input-costs`}
          />
          <BarSelect
            iconName={CONTROL_ICONS.outputPrices}
            label={t("forecastSettingsBar.outputCosts")}
            value={presetFromPct(settings.market, OUTPUT_COST_PRESET_PCT) ?? FORECAST_PRESET_CUSTOM}
            options={presetOptions(settings.market, OUTPUT_COST_PRESET_PCT, t)}
            onChange={(preset) => handleChange("market", pctFromPreset(preset as ForecastPreset, OUTPUT_COST_PRESET_PCT))}
            dataTestId={`${dataTestId}-output-costs`}
          />
          <BarSelect
            iconName={CONTROL_ICONS.timeline}
            label={t("forecastSettingsBar.timeline")}
            value={settings.timeline}
            options={TIMELINE_VALUES.map((value) => ({ value, label: t("forecastSettingsBar.timelineOption", { years: value }) }))}
            /* Coerced: `settings.timeline` feeds the projection arithmetic, so it must stay numeric. */
            onChange={(value) => handleChange("timeline", Number(value) as TimelineOption)}
            dataTestId={`${dataTestId}-timeline`}
          />

          <button type="button" className="forecast-settings-bar-more-filters" onClick={onMoreFilters} data-testid={`${dataTestId}-more-filters`}>
            <Icon iconName="tabler/adjustments-horizontal" size="s" className="forecast-settings-bar-more-filters-icon" aria-hidden />
            {t("forecastSettingsBar.moreFilters")}
          </button>
        </div>

        <div className="forecast-settings-bar-actions">
          {showScenarioActions ? (
            <>
              <Tooltip title={t("forecastSettingsBar.addScenario")} placement="top">
                <Button
                  type="secondary"
                  size="middle"
                  onClick={onAddScenario}
                  disabled={disableAddScenario}
                  icon={<Icon iconName="klim/plus" size={20} />}
                  iconLeft
                  className="forecast-settings-bar-add-scenario"
                  dataTestId={`${dataTestId}-add-scenario`}
                >
                  <span className="forecast-settings-bar-action-label">{t("forecastSettingsBar.addScenario")}</span>
                </Button>
              </Tooltip>
              <Tooltip title={t("forecastSettingsBar.editScenarios")} placement="top">
                <Button
                  type="secondary"
                  size="middle"
                  onClick={onEditScenarios}
                  icon={<Icon iconName="tabler/pencil" size={20} />}
                  iconLeft
                  className="forecast-settings-bar-edit-scenarios"
                  dataTestId={`${dataTestId}-edit-scenarios`}
                >
                  <span className="forecast-settings-bar-action-label">{t("forecastSettingsBar.editScenarios")}</span>
                </Button>
              </Tooltip>
            </>
          ) : null}
          {renderPrimaryAction()}
        </div>
      </div>
    </Form>
  );
};

export default ForecastSettingsBar;
